import { useCallback, useEffect, useState } from "react";

import { Patient, Reference } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { DevelopmentFeatureFlags } from "@/feature-flags/constants/feature-flags";
import useFeature from "@/feature-flags/hooks/useFeature";
import {
  getRelatedNewbornsForMother,
  RelatedNewborn,
} from "@/newborn-patient-linking/services/get-related-newborns-for-mother";
import { getActiveOrganizationId, joinReferenceString } from "@/utils/medplum";

export type NewbornFlowsheetSubjectStatus =
  | "not-applicable"
  | "loading"
  | "ready"
  | "ambiguous"
  | "none"
  | "error";

interface UseNewbornFlowsheetSubjectParams {
  /** True only for a "newborn"-domain flowsheet questionnaire; a "client"-domain one never resolves. */
  enabled: boolean;
  motherId: string | undefined;
  laborEncounterId: string | undefined;
}

interface UseNewbornFlowsheetSubjectResult {
  status: NewbornFlowsheetSubjectStatus;
  /** Only set once `status` is `"ready"`. */
  newbornPatientReference: Reference<Patient> | undefined;
  /** Populated when `status` is `"ambiguous"`, for rendering a picker. */
  candidates: RelatedNewborn[];
  selectNewborn: (reference: Reference<Patient>) => void;
  retry: () => void;
}

/**
 * Resolves which newborn a "newborn"-domain flowsheet entry (charted from the Labor & Delivery
 * encounter's own Postpartum tab) should be attributed to, via the same delivery-scoped lookup
 * (`getRelatedNewbornsForMother` with `laborEncounterId`) used elsewhere for this feature. Gated on
 * the `NewbornPatientLinking` flag: while it's off, this reports `"not-applicable"` so the caller
 * keeps writing against the mother exactly as it does today, matching how the newborn-domain bots
 * preserve their own legacy behavior on the flag-off path.
 *
 * Reports `"none"` rather than falling back to the mother when zero newborns are discovered yet (e.g.
 * Birthing hasn't been charted) — a caller should block the entry with an explanatory message instead
 * of silently attributing newborn clinical data to the mother's own Patient. Reports `"ambiguous"`
 * when more than one newborn is discovered (a delivery with twins), so a caller can show `NewbornPicker`
 * to disambiguate rather than guessing.
 */
export function useNewbornFlowsheetSubject({
  enabled,
  motherId,
  laborEncounterId,
}: UseNewbornFlowsheetSubjectParams): UseNewbornFlowsheetSubjectResult {
  const medplum = useMedplum();
  const isNewbornPatientLinkingEnabled = useFeature(DevelopmentFeatureFlags.NewbornPatientLinking);
  const [resolution, setResolution] = useState<NewbornResolution | undefined>();
  const [requestToken, setRequestToken] = useState(0);

  const shouldResolve =
    enabled && isNewbornPatientLinkingEnabled && Boolean(motherId) && Boolean(laborEncounterId);

  useEffect(() => {
    // Cleared while disabled, so reopening the drawer starts from "loading" instead of the last pick.
    if (!shouldResolve) {
      setResolution(undefined);
      return;
    }

    let isActive = true;
    const deliveryKey = toDeliveryKey(motherId, laborEncounterId);
    setResolution({ deliveryKey, status: "loading", candidates: [] });

    (async () => {
      try {
        const relatedNewborns = await getRelatedNewbornsForMother(
          medplum,
          { reference: joinReferenceString("Patient", motherId!) },
          getActiveOrganizationId(),
          { laborEncounterId },
        );
        if (!isActive) return;
        setResolution(resolveFromCandidates(deliveryKey, relatedNewborns));
      } catch {
        if (!isActive) return;
        setResolution({ deliveryKey, status: "error", candidates: [] });
      }
    })();

    return () => {
      isActive = false;
    };
  }, [shouldResolve, medplum, motherId, laborEncounterId, requestToken]);

  const selectNewborn = useCallback((reference: Reference<Patient>) => {
    setResolution(
      (current) => current && { ...current, status: "ready", selectedReference: reference },
    );
  }, []);

  const retry = useCallback(() => setRequestToken((previous) => previous + 1), []);

  // A resolution for a different delivery reads as "loading" until the effect re-resolves.
  const currentResolution =
    resolution?.deliveryKey === toDeliveryKey(motherId, laborEncounterId) ? resolution : undefined;

  let status: NewbornFlowsheetSubjectStatus;
  if (!shouldResolve) {
    status = "not-applicable";
  } else {
    status = currentResolution?.status ?? "loading";
  }

  return {
    status,
    newbornPatientReference: status === "ready" ? currentResolution?.selectedReference : undefined,
    candidates: shouldResolve && currentResolution ? currentResolution.candidates : [],
    selectNewborn,
    retry,
  };
}

/**
 * Builds the resolved state for one delivery from its discovered newborns.
 *
 * @param deliveryKey - Key of the delivery these candidates were discovered for
 * @param candidates - Newborns discovered for that delivery
 * @returns "ready" with the sole newborn selected, "none" for zero, or "ambiguous" for more than one
 */
function resolveFromCandidates(
  deliveryKey: string,
  candidates: RelatedNewborn[],
): NewbornResolution {
  if (candidates.length === 1) {
    return {
      deliveryKey,
      status: "ready",
      candidates,
      selectedReference: candidates[0].patientReference,
    };
  }
  return { deliveryKey, status: candidates.length === 0 ? "none" : "ambiguous", candidates };
}

function toDeliveryKey(motherId: string | undefined, laborEncounterId: string | undefined): string {
  return `${motherId}|${laborEncounterId}`;
}

interface NewbornResolution {
  deliveryKey: string;
  status: Exclude<NewbornFlowsheetSubjectStatus, "not-applicable">;
  candidates: RelatedNewborn[];
  selectedReference?: Reference<Patient>;
}
